import { ContactDetailsSectionDisplayableEntry } from '@/types/contactDetailsSection';
import { TranslatableFiatAccountType } from '@/types/contacts';
import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { RecipientResponse, ReviewStatus } from '@/types/recipient';
import { shortenWalletAddress } from '@/utility/shortenWalletAddress';

export const isRecipientAvailableForTransaction = (recipient: RecipientResponse): boolean => {
  return recipient.reviewStatus == null || recipient.reviewStatus === ReviewStatus.APPROVED;
};

export const getRecipientDisplayName = (recipient: RecipientResponse): string => {
  return recipient.individualName ?? recipient.businessName ?? '';
};

export const getRecipientBankName = (recipient: RecipientResponse): string | undefined => {
  return recipient.details.bankName as string | undefined;
};

export const getRecipientAccountNumber = (recipient: RecipientResponse): string | undefined => {
  switch (recipient.type) {
    case PaymentRailType.ACH:
    case PaymentRailType.Wire:
    case PaymentRailType.RTP:
    case PaymentRailType.Swift:
      return (recipient.details.accountNumber ?? recipient.details.iban) as string | undefined;
    case PaymentRailType.PIX:
      return recipient.details.pixKey as string | undefined;
    case PaymentRailType.SPEI:
      return recipient.details.clabe as string | undefined;
    case PaymentRailType.CVU:
      return recipient.details.cvu as string | undefined;
    case PaymentRailType.COP:
      return recipient.details.bankAccount as string | undefined;
    case PaymentRailType.Ethereum:
    case PaymentRailType.TRON:
      return recipient.details.walletAddress as string | undefined;
    default:
      return undefined;
  }
};

export const getRecipientCurrency = (recipient: RecipientResponse): CurrencyCodeType => {
  return recipient.currency;
};

export interface RecipientPrimaryIdentifier {
  readonly label?: string;
  readonly value?: string;
}

const FALLBACK_IDENTIFIER_KEYS: readonly (readonly [string, string])[] = [
  ['iban', 'IBAN'],
  ['accountNumber', 'ACCOUNT_NUMBER'],
  ['clabe', 'CLABE'],
  ['cvu', 'CVU'],
  ['msisdn', 'MOBILE_NUMBER'],
  ['walletAddress', 'CRYPTO_WALLET_ADDRESS'],
];

const getRecipientFallbackIdentifier = (
  recipient: RecipientResponse,
): RecipientPrimaryIdentifier => {
  const { details } = recipient;
  for (const [key, label] of FALLBACK_IDENTIFIER_KEYS) {
    const value = details[key] as string | undefined;
    if (value) {
      return { label, value };
    }
  }
  return {};
};

export const shortenIdentifier = (
  label: string | undefined,
  value: string | undefined,
): string | undefined =>
  label === 'CRYPTO_WALLET_ADDRESS' && value ? shortenWalletAddress(value) : value;

export type RecipientNetworkIcon = 'ethereum' | 'tron' | 'polygon';

export interface RecipientRecordEntry {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly translateValue?: boolean;
  readonly icon?: RecipientNetworkIcon;
}

export const getRecipientNetworkIcon = (
  recipient: RecipientResponse,
): RecipientNetworkIcon | undefined => {
  switch (recipient.type) {
    case PaymentRailType.Ethereum:
      return 'ethereum';
    case PaymentRailType.TRON:
      return 'tron';
    default:
      return undefined;
  }
};

const formatRecipientAddress = (address: unknown): string | undefined => {
  if (typeof address === 'string') {
    return address.trim() === '' ? undefined : address;
  }
  if (address != null && typeof address === 'object') {
    const parts = address as Record<string, unknown>;
    const ordered = [parts.street1, parts.street2, parts.city, parts.state].filter(
      (part: unknown): part is string => typeof part === 'string' && part.trim() !== '',
    );
    return ordered.length > 0 ? ordered.join(', ') : undefined;
  }
  return undefined;
};

const getRecipientBankOrNetwork = (recipient: RecipientResponse): string | undefined => {
  switch (recipient.type) {
    case PaymentRailType.Ethereum:
      return 'Ethereum';
    case PaymentRailType.TRON:
      return 'Tron';
    default:
      return getRecipientBankName(recipient);
  }
};

const getRecipientSecondaryIdentifier = (recipient: RecipientResponse): string | undefined => {
  const { details } = recipient;
  switch (recipient.type) {
    case PaymentRailType.ACH:
    case PaymentRailType.RTP:
    case PaymentRailType.Wire:
      return details.routingNumber as string | undefined;
    case PaymentRailType.Swift:
    case PaymentRailType.Cnaps:
    case PaymentRailType.Chats:
      return details.swiftCode as string | undefined;
    case PaymentRailType.PIX:
    case PaymentRailType.SPEI:
    case PaymentRailType.CVU:
      return details.documentNumber as string | undefined;
    case PaymentRailType.COP:
      return (details.documentNumber ?? details.bankCode) as string | undefined;
    case PaymentRailType.Ethereum:
    case PaymentRailType.TRON:
      return details.vaspId as string | undefined;
    default:
      return (details.documentNumber ?? details.routingNumber ?? details.swiftCode) as
        | string
        | undefined;
  }
};

// The secondary record shown under the primary identifier: holder + routing
// context, never repeating the name, nickname, currency, or primary identifier
// already surfaced in the field and detail header.
export const getRecipientAccountHolderType = (
  recipient: RecipientResponse,
): 'INDIVIDUAL' | 'BUSINESS' | undefined =>
  recipient.individualName ? 'INDIVIDUAL' : recipient.businessName ? 'BUSINESS' : undefined;

export const getRecipientSecondaryEntries = (
  recipient: RecipientResponse,
): readonly RecipientRecordEntry[] => {
  const { details } = recipient;
  const entries: RecipientRecordEntry[] = [];

  const recipientAddress = formatRecipientAddress(details.recipientAddress);
  if (recipientAddress) {
    entries.push({ key: 'recipientAddress', label: 'RECIPIENT_ADDRESS', value: recipientAddress });
  }

  const bankOrNetwork = getRecipientBankOrNetwork(recipient);
  if (bankOrNetwork) {
    entries.push({
      key: 'bank',
      label: 'BANK_NETWORK',
      value: bankOrNetwork,
      icon: getRecipientNetworkIcon(recipient),
    });
  }

  const secondaryIdentifier = getRecipientSecondaryIdentifier(recipient);
  if (secondaryIdentifier) {
    entries.push({ key: 'identifier', label: 'IDENTIFIER', value: secondaryIdentifier });
  }

  const bankAddress = formatRecipientAddress(details.bankAddress);
  if (bankAddress) {
    entries.push({ key: 'bankAddress', label: 'BANK_ADDRESS', value: bankAddress });
  }

  return entries;
};

export const getRecipientPrimaryIdentifier = (
  recipient: RecipientResponse,
): RecipientPrimaryIdentifier => {
  const { details } = recipient;
  switch (recipient.type) {
    case PaymentRailType.ACH:
    case PaymentRailType.Wire:
    case PaymentRailType.RTP:
    case PaymentRailType.Swift: {
      const accountNumber = details.accountNumber as string | undefined;
      const iban = details.iban as string | undefined;
      return accountNumber
        ? { label: 'ACCOUNT_NUMBER', value: accountNumber }
        : { label: 'IBAN', value: iban };
    }
    case PaymentRailType.PIX:
      return { label: 'PIX_KEY', value: details.pixKey as string | undefined };
    case PaymentRailType.SPEI:
      return { label: 'CLABE', value: details.clabe as string | undefined };
    case PaymentRailType.CVU:
      return { label: 'CVU', value: details.cvu as string | undefined };
    case PaymentRailType.COP:
      return { label: 'BANK_ACCOUNT', value: details.bankAccount as string | undefined };
    case PaymentRailType.Ethereum:
    case PaymentRailType.TRON:
      return { label: 'CRYPTO_WALLET_ADDRESS', value: details.walletAddress as string | undefined };
    default:
      return getRecipientFallbackIdentifier(recipient);
  }
};

export const getRecipientDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  switch (recipient.type) {
    case PaymentRailType.Wire:
      return buildWireDisplayEntries(recipient);
    case PaymentRailType.ACH:
    case PaymentRailType.RTP:
      return buildACHDisplayEntries(recipient);
    case PaymentRailType.Swift:
    case PaymentRailType.Cnaps:
    case PaymentRailType.Chats:
      return buildSwiftDisplayEntries(recipient);
    case PaymentRailType.PIX:
      return buildPixDisplayEntries(recipient);
    case PaymentRailType.COP:
      return buildCOPDisplayEntries(recipient);
    case PaymentRailType.SPEI:
      return buildSpeiDisplayEntries(recipient);
    case PaymentRailType.CVU:
      return buildCVUDisplayEntries(recipient);
    case PaymentRailType.Ethereum:
    case PaymentRailType.TRON:
      return buildCryptoDisplayEntries(recipient);
    default:
      return [];
  }
};

export const getRecipientAccountTypeLabel = (
  recipient: RecipientResponse,
): TranslatableFiatAccountType => {
  switch (recipient.type) {
    case PaymentRailType.ACH:
      return 'FIAT_ACCOUNT_ACH';
    case PaymentRailType.Wire:
      return 'FIAT_ACCOUNT_WIRE';
    case PaymentRailType.RTP:
      return 'FIAT_ACCOUNT_RTP';
    case PaymentRailType.Swift:
      return 'FIAT_ACCOUNT_SWIFT';
    case PaymentRailType.Cnaps:
      return 'FIAT_ACCOUNT_CNAPS';
    case PaymentRailType.Chats:
      return 'FIAT_ACCOUNT_CHATS';
    case PaymentRailType.PIX:
      return 'FIAT_ACCOUNT_PIX';
    case PaymentRailType.COP:
      return 'FIAT_ACCOUNT_COP';
    case PaymentRailType.SPEI:
      return 'FIAT_ACCOUNT_SPEI';
    case PaymentRailType.CVU:
      return 'FIAT_ACCOUNT_CVU';
    case PaymentRailType.Ethereum:
    case PaymentRailType.TRON:
      return 'FIAT_ACCOUNT_CRYPTO';
    default:
      return '';
  }
};

const buildWireDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const bankName = details.bankName as string | undefined;
  const routingNumber = details.routingNumber as string | undefined;
  const accountNumber = details.accountNumber as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (bankName) {
    entries.push({
      key: 'bankName',
      displayValue: `${bankName} (USD)`,
    });
  }

  if (routingNumber) {
    entries.push({
      key: 'routingNumber',
      label: 'ROUTING_NUMBER_DETAILS',
      displayValue: routingNumber,
    });
  }

  if (accountNumber) {
    entries.push({
      key: 'accountNumber',
      label: 'ACCOUNT_NUMBER_DETAILS',
      displayValue: accountNumber,
    });
  }

  return entries;
};

const buildACHDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const bankName = details.bankName as string | undefined;
  const routingNumber = details.routingNumber as string | undefined;
  const accountNumber = details.accountNumber as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (bankName) {
    entries.push({
      key: 'bankName',
      displayValue: `${bankName} (USD)`,
    });
  }

  if (routingNumber) {
    entries.push({
      key: 'routingNumber',
      label: 'ROUTING_NUMBER_DETAILS',
      displayValue: routingNumber,
    });
  }

  if (accountNumber) {
    entries.push({
      key: 'accountNumber',
      label: 'ACCOUNT_NUMBER_DETAILS',
      displayValue: accountNumber,
    });
  }

  return entries;
};

const buildSwiftDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details, currency } = recipient;
  const bankName = details.bankName as string | undefined;
  const swiftCode = details.swiftCode as string | undefined;
  const iban = details.iban as string | undefined;
  const accountNumber = details.accountNumber as string | undefined;
  const bankCode = details.bankCode as string | undefined;
  const mobileNumber = details.mobileNumber as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (bankName) {
    entries.push({
      key: 'bankName',
      displayValue: `${bankName} (${currency})`,
    });
  }

  if (swiftCode) {
    entries.push({
      key: 'swiftCode',
      label: 'SWIFT_CODE',
      displayValue: swiftCode,
    });
  }

  if (iban || accountNumber) {
    entries.push({
      key: 'iban',
      label: iban ? 'IBAN' : 'ACCOUNT_NUMBER',
      displayValue: (iban ?? accountNumber) as string,
    });
  }

  // CNAPS is CNY-only — legacy USD-CN recipients may still carry a stale
  // `bankCode`, but we never want to surface it as a CNAPS code on a USD rail.
  if (bankCode && currency === CurrencyCodeType.CNY) {
    entries.push({
      key: 'bankCode',
      label: 'BANK_CODE_CNAPS',
      displayValue: bankCode,
    });
  }

  // Mobile number belongs to the local-currency rail only (CN+CNY, HK+HKD).
  // Legacy USD-CN/HK recipients may have a stale value persisted; suppress it
  // there. HKD/CNY are country-exclusive currencies, so a currency check is
  // sufficient.
  const isLocalRail = currency === CurrencyCodeType.CNY || currency === CurrencyCodeType.HKD;
  if (mobileNumber && isLocalRail) {
    entries.push({
      key: 'mobileNumber',
      label: 'MOBILE_NUMBER',
      displayValue: mobileNumber,
    });
  }

  return entries;
};

const buildPixDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const pixKey = details.pixKey as string | undefined;
  const documentNumber = details.documentNumber as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (pixKey) {
    entries.push({
      key: 'pixKey',
      label: 'PIX_KEY',
      displayValue: pixKey,
    });
  }

  if (documentNumber) {
    entries.push({
      key: 'documentNumber',
      label: 'DOCUMENT_NUMBER_CPF_CNPJ',
      displayValue: documentNumber,
    });
  }

  return entries;
};

const buildCOPDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const bankCode = details.bankCode as string | undefined;
  const accountType = details.accountType as string | undefined;
  const bankAccount = details.bankAccount as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (bankCode) {
    entries.push({
      key: 'bankCode',
      label: 'BANK_CODE',
      displayValue: bankCode,
    });
  }

  if (accountType) {
    entries.push({
      key: 'accountType',
      label: 'ACCOUNT_TYPE',
      displayValue: accountType,
    });
  }

  if (bankAccount) {
    entries.push({
      key: 'bankAccount',
      label: 'BANK_ACCOUNT',
      displayValue: bankAccount,
    });
  }

  return entries;
};

const buildSpeiDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const clabe = details.clabe as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (clabe) {
    entries.push({
      key: 'clabe',
      label: 'CLABE',
      displayValue: clabe,
    });
  }

  return entries;
};

const buildCVUDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const cvu = details.cvu as string | undefined;

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  if (cvu) {
    entries.push({
      key: 'cvu',
      displayValue: cvu,
    });
  }

  return entries;
};

const buildCryptoDisplayEntries = (
  recipient: RecipientResponse,
): readonly ContactDetailsSectionDisplayableEntry[] => {
  const { details } = recipient;
  const walletAddress = details.walletAddress as string | undefined;
  const network = recipient.type === PaymentRailType.Ethereum ? 'Ethereum' : 'Tron';

  const entries: ContactDetailsSectionDisplayableEntry[] = [];

  entries.push({
    key: 'network',
    label: 'CRYPTO_NETWORK',
    displayValue: `${network} (${recipient.currency})`,
  });

  if (walletAddress) {
    entries.push({
      key: 'walletAddress',
      label: 'CRYPTO_WALLET_ADDRESS',
      displayValue: walletAddress,
    });
  }

  return entries;
};
